import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import '../App.css';

interface HeaderProps {
  isAdmin?: boolean;
}

const AvatarMenu: React.FC<{ isAdmin?: boolean; onSignOut: () => void }> = ({ isAdmin, onSignOut }) => {
  const [open, setOpen] = useState(false);
  const [initials, setInitials] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchUserAttributes()
      .then(attrs => {
        const name = (attrs.name ?? '').trim();
        if (name) {
          const parts = name.split(' ');
          setInitials(
            parts.length >= 2
              ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
              : name.substring(0, 2).toUpperCase()
          );
        } else if (attrs.email) {
          setInitials(attrs.email.substring(0, 2).toUpperCase());
        }
      })
      .catch(() => setInitials('?'));
  }, []);

  useEffect(() => {
    const onMouse = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <div className="nav-avatar-wrapper" ref={ref}>
      <button
        className="nav-avatar"
        onClick={() => setOpen(o => !o)}
        aria-label="Account menu"
        aria-expanded={open}
      >
        {initials}
      </button>
      {open && (
        <div className="nav-avatar-dropdown">
          <Link to="/account" onClick={() => setOpen(false)}>Account</Link>
          {isAdmin && <Link to="/admin" onClick={() => setOpen(false)}>Admin</Link>}
          <button onClick={() => { setOpen(false); onSignOut(); }}>Sign Out</button>
        </div>
      )}
    </div>
  );
};

const Header: React.FC<HeaderProps> = ({ isAdmin }) => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const navigate = useNavigate();
  const location = useLocation();
  // Mobile nav drawer. Hidden entirely on desktop via CSS — the menubar renders
  // exactly as before at >768px.
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the drawer whenever the route changes (tapping a link navigates).
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Close on Escape for keyboard users.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleSignOut = () => {
    // Through a route, so a page holding unsaved work can stop it (#848).
    navigate('/signout');
  };

  return (
    <div className="global-header-wrapper">
      <header className="global-header">
        <div className="header-container">
          <div className="header-left">
            <Link to="/" className="logo-link">
              <img src="/chaptery-mark.svg" alt="Chaptery Logo" className="header-logo" />
            </Link>
          </div>
          <div className="header-center">
            <img src="/img/HeaderGraphic.PNG" alt="A Novel Approach to Organization" className="header-graphic" />
          </div>
          <div className="header-right" />
        </div>
      </header>

      <nav className="main-menubar">
        <div className="menubar-container">
          <button
            type="button"
            className="menubar-toggle"
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={menuOpen}
            aria-controls="primary-menu"
            onClick={() => setMenuOpen(o => !o)}
          >
            <span className="menubar-toggle-bar" />
            <span className="menubar-toggle-bar" />
            <span className="menubar-toggle-bar" />
          </button>
          <div id="primary-menu" className={`menubar-start${menuOpen ? ' open' : ''}`}>
            {authStatus === 'authenticated' ? (
              <>
                <Link to="/books" className="menubar-item">My Bookshelf</Link>
                <Link to="/world" className="menubar-item">World Building</Link>
                <Link to="/storyboard" className="menubar-item">Storyboard</Link>
                <Link to="/writing-dashboard" className="menubar-item">Writing Dashboard</Link>
                <Link to="/downloads" className="menubar-item">Downloads</Link>
                <Link to="/help" className="menubar-item">Help</Link>
              </>
            ) : (
              <>
                <Link to="/features" className="menubar-item">Features</Link>
                <Link to="/compare" className="menubar-item">Compare</Link>
                <Link to="/pricing" className="menubar-item">Pricing</Link>
                <Link to="/help" className="menubar-item">Help</Link>
              </>
            )}
          </div>
          <div className="menubar-end">
            {authStatus === 'authenticated' ? (
              <AvatarMenu isAdmin={isAdmin} onSignOut={handleSignOut} />
            ) : (
              <>
                <Link to="/login" className="menubar-item">Sign In</Link>
                <Link to="/pricing" className="menubar-item menubar-signup">Get Started →</Link>
              </>
            )}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Header;
